<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { Copy, Check, ChevronDown, ScanEye, RotateCcw } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { formatTimeFromNow } from '@/utils/datetime'
import { formatDate, formatNumber, normalizeUnits, statusGroup, requestDateRow } from './format'
import LayerRequestParams from './LayerRequestParams.vue'
import LayerGeometryStats from './LayerGeometryStats.vue'
import type { PendingLayer } from './types'

const props = defineProps<{
  layer: PendingLayer
  orgName: string
  selected: boolean
  processing?: boolean
}>()

const store = useStore()
const tz = computed<string | null>(() => store.getters['user/getUserSettings']?.tz ?? null)

const emit = defineEmits<{ (e: 'select'): void; (e: 'zoom'): void; (e: 'reset'): void }>()

const copied = ref(false)
const detailsOpen = ref(false)

const rejected = computed(() => props.layer.status === -100)
const resettable = computed(() =>
  props.layer.status < 0 && (!props.layer.planet_id || (props.layer.layer_type === 'tasking' && props.layer.status === -4)))
const dateRow = computed(() => requestDateRow(props.layer, tz.value))
const requester = computed(() =>
  props.layer.member_name ? `${props.layer.member_name} (${props.layer.member_email})` : props.layer.member_email)

const statusVariant = computed(() => {
  const group = statusGroup(props.layer.status)
  if (group === 'done') return 'default'
  if (group === 'failed') return 'destructive'
  return 'secondary'
})

const estimate = computed(() => props.layer.data?.estimate ?? null)

const copyPlanetId = async () => {
  if (!props.layer.planet_id) return
  try {
    await navigator.clipboard.writeText(props.layer.planet_id)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1500)
  } catch (e) { /* clipboard unavailable */ }
}
</script>

<template>
  <div
    :class="['border rounded-lg bg-card p-4 cursor-pointer transition-colors hover:border-primary', selected ? 'border-primary' : '']"
    @click="emit('select')"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0 flex-wrap">
        <Badge :variant="rejected ? 'destructive' : 'default'">{{ rejected ? 'Rejected' : 'Approved' }}</Badge>
        <Badge v-if="!rejected" :variant="statusVariant">{{ layer.status_label }}</Badge>
        <span class="font-medium truncate">{{ layer.layer_name || 'Unnamed Layer' }}</span>
      </div>
      <div class="flex items-center shrink-0">
        <Button v-if="resettable" variant="ghost" size="sm" title="Move back to pending approval" :disabled="processing" @click.stop="emit('reset')">
          <RotateCcw class="h-4 w-4 mr-1" />
          Reset
        </Button>
        <Button variant="ghost" size="sm" title="Zoom to AOI" :disabled="!layer.geometry" @click.stop="emit('zoom')">
          <ScanEye class="h-4 w-4 mr-1" />
          Zoom
        </Button>
      </div>
    </div>

    <div class="mt-2 space-y-1 text-sm">
      <div class="font-medium truncate">{{ orgName }}</div>
      <div class="truncate">{{ requester || '--' }}</div>
      <div><span>Requested {{ formatDate(layer.created_at, tz) }}<span class="text-muted-foreground"> · {{ formatTimeFromNow(layer.created_at) }}</span><template v-if="layer.approved_at"><span class="text-muted-foreground"> · processed {{ formatDate(layer.approved_at, tz) }}</span></template></span></div>
      <div v-if="dateRow"><span>{{ dateRow.label }} {{ dateRow.value }}</span></div>
      <div v-if="layer.rejection_reason" class="text-destructive"><span>Rejected: {{ layer.rejection_reason }}</span></div>
    </div>

    <Collapsible v-model:open="detailsOpen" class="mt-5">
      <CollapsibleTrigger class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" @click.stop>
        <ChevronDown :class="['h-3 w-3 transition-transform', { 'rotate-180': detailsOpen }]" />
        Details
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2 pt-2 border-t border-border/50">
          <div class="text-xs space-y-0.5">
            <div class="text-muted-foreground font-medium">Planet</div>
            <div v-if="layer.planet_id" class="flex items-center gap-1">
              <span class="truncate"><span class="text-muted-foreground">ID:</span> <code class="font-mono">{{ layer.planet_id }}</code></span>
              <Button variant="ghost" size="icon" class="h-5 w-5 shrink-0" title="Copy Planet ID" @click.stop="copyPlanetId">
                <Check v-if="copied" class="h-3 w-3 text-green-600" />
                <Copy v-else class="h-3 w-3" />
              </Button>
            </div>
            <div v-else-if="!rejected" class="text-muted-foreground italic">Not yet submitted to Planet</div>
            <div v-if="layer.layer_type === 'tasking' && !rejected"><span class="text-muted-foreground">Captures:</span> {{ layer.captures_count ?? 0 }}</div>
            <div v-if="estimate?.estimated_cost != null">
              <span class="text-muted-foreground">Estimate at request:</span> {{ formatNumber(estimate.estimated_cost, 2) }} {{ normalizeUnits(estimate.units) || 'credits' }}
            </div>
            <div v-if="layer.updated_at"><span class="text-muted-foreground">Updated:</span> {{ formatTimeFromNow(layer.updated_at) }}</div>
            <div v-if="layer.error" class="pt-1">
              <div class="text-destructive">Error</div>
              <pre class="text-[11px] bg-muted p-2 rounded overflow-auto max-h-40 mt-1">{{ JSON.stringify(layer.error, null, 2) }}</pre>
            </div>
          </div>
          <LayerRequestParams :layer="layer" />
          <LayerGeometryStats :geometry="layer.geometry" :planet-area-km2="estimate?.area_km2 ?? null" />
        </div>
      </CollapsibleContent>
    </Collapsible>
  </div>
</template>
